/**
 * État d'interface local (jamais envoyé au serveur), partagé entre le HUD
 * (Preact) et le moteur de rendu : sélection, mode construction, calques,
 * fenêtre de direction, notifications. Les signaux évitent de re-rendre tout
 * le HUD : seuls les composants qui lisent un signal se mettent à jour.
 */
import { signal } from '@preact/signals';
import type { AlertLevel, BuildingKind, Difficulty, LiftKind, NodeKey, PisteRoute, PisteSize, Vec2 } from '@snowchain/shared';

export type Selection = { kind: 'lift' | 'piste' | 'building' | 'groomer'; id: number } | null;

export type BuildMode =
  | null
  | { kind: 'building'; type: BuildingKind; rot: number }
  | { kind: 'lift'; type: LiftKind; bottom: Vec2 | null; difficulty: Difficulty | 'auto' }
  /** Tracé d'une piste : points posés (départ en premier), mode de tracé et gabarit. */
  | { kind: 'piste'; points: Vec2[]; route: PisteRoute; size: PisteSize };

/** Reprise du tracé d'une piste existante (points de passage déplaçables). */
export interface PisteEdit {
  id: number;
  points: Vec2[];
  route: PisteRoute;
  size: PisteSize;
  /** Fermeture demandée : la reprise part dès que la piste est vide. */
  waiting: boolean;
}

/** Vue « où peut-on aller » : réseau atteint depuis un nœud, pour un niveau. */
export interface ReachView {
  from: NodeKey;
  label: string;
  skill: 0 | 1 | 2 | null;
}

export interface GhostInfo {
  ok: boolean;
  reason?: string;
  cost: number;
  detail?: string;
}

export interface Toast {
  id: number;
  level: AlertLevel;
  title: string;
  message?: string;
}

/** Onglets de la fenêtre « Direction de la station ». */
export type OfficeTab = 'frequentation' | 'finances' | 'stocks' | 'tarifs' | 'journal';

/** Onglets du panneau d'affluence en direct. */
export type LiveTab = 'remontees' | 'batiments' | 'pistes';

export const ui = {
  selection: signal<Selection>(null),
  build: signal<BuildMode>(null),
  buildMenu: signal(false),
  overlay: signal(false),
  /** Reprise du tracé d'une piste en cours, ou null. */
  pisteEdit: signal<PisteEdit | null>(null),
  /** Réseau mis en évidence (où peut-on aller depuis ce point), ou null. */
  reach: signal<ReachView | null>(null),
  /** Panneau de gestion des pistes (liste, réseau, défauts). */
  pistesPanel: signal(false),
  /** Plan des pistes façon plan papier (plein écran). */
  trailMap: signal(false),
  /** Fenêtre de direction ouverte (et son onglet), ou null. */
  office: signal<OfficeTab | null>(null),
  /** Épingles d'affluence au-dessus des installations. */
  pins: signal(true),
  /** Onglet de la fiche de sélection. */
  inspectorTab: signal<'apercu' | 'historique'>('apercu'),
  /** Service affiché pour un bâtiment qui en a plusieurs (chalet : caisses ou restaurant). */
  inspectorService: signal<string | null>(null),
  liveTab: signal<LiveTab>('remontees'),
  /** Jour affiché dans le rapport de fréquentation (null : aujourd'hui, 0 : saison). */
  reportDay: signal<number | null>(null),
  /** Installation détaillée dans le rapport (clé d'usage). */
  reportKey: signal<string | null>(null),
  perf: signal(new URLSearchParams(location.search).has('perf')),
  freeCam: signal(false),
  /** Dameuse suivie par la caméra (identifiant), ou null. */
  follow: signal<number | null>(null),
  /** Panneau « Damage » (plan de la nuit et flotte) ouvert. */
  grooming: signal(false),
  ghost: signal<GhostInfo | null>(null),
  toasts: signal<Toast[]>([]),
  /** Incrémenté (au plus ~6 fois/s) quand l'état serveur change. */
  tick: signal(0),
  /** Statistiques de rendu (mises à jour 2 fois/s). */
  perfStats: signal<Record<string, number | string>>({}),
};

let toastId = 1;
export function toast(level: AlertLevel, title: string, message?: string, ms = 4500): void {
  const t: Toast = { id: toastId++, level, title, message };
  ui.toasts.value = [...ui.toasts.value.slice(-3), t];
  setTimeout(() => {
    ui.toasts.value = ui.toasts.value.filter((x) => x.id !== t.id);
  }, ms);
}
